<script lang="ts" setup>
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import CheckInAction from "@/views/trips/view/components/CheckInAction.vue";
import GoodToGoAction from "@/views/trips/view/components/GoodToGoAction.vue";
import LoadVerificationAction from "@/views/trips/view/components/LoadVerificationAction.vue";
import UnloadVerificationAction from "@/views/trips/view/components/UnloadVerificationAction.vue";
import { RouteStatus, RouteStopState, RouteStopStatus } from "@/constants/trip";
import { OrderStopType } from "@/constants/order";
import RouteStopStatusLabel from "@/views/trips/components/RouteStopStatus.vue";
import StepperItem from "@/views/trips/components/StepperItem.vue";
import DistanceLine from "@/views/trips/components/DistanceLine.vue";
import StopTimer from "@/views/trips/components/StopTimer.vue";
import RouteStopFreightDetails from "@/views/trips/components/RouteStopFreightDetails.vue";
import RouteStopActions from "@/views/trips/components/RouteStopActions.vue";
import LoadAction from "@/views/trips/view/components/LoadAction.vue";
import UnloadAction from "@/views/trips/view/components/UnloadAction.vue";
import ShowInfoAction from "@/views/trips/view/components/ShowInfoAction.vue";
import AddBolAction from "@/views/trips/view/components/AddBolAction.vue";
import FinishAction from "@/views/trips/view/components/FinishAction.vue";
import StopChangesPopover from "@/views/trips/components/StopChangesPopover.vue";
import FacilityInfo from "@/views/trips/components/FacilityInfo.vue";
import AlertMessage from "@/components/messages/AlertMessage.vue";
import OrderActionsTime from "@/components/order/OrderActionsTime.vue";
import RouteStopIcon from "@/components/icons/RouteStopIcon.vue";
import TooltipButton from "@/components/buttons/TooltipButton.vue";
import { useFreights, useTrip } from "@/composables";
import { useDisableRules } from "@/composables/reactive/useDisableRules";
import type { RouteStopDto, TripDto, TripRouteDto } from "@/api/trips/dto/trip.dto";
import type { TripRouteFreight } from "@/types/trip";
import type { OrderFreightDto } from "@/api/orders/dto/order.dto";
import type { DisableRule } from "@/types/validation";
import SignPodAction from "@/views/trips/view/components/SignPodAction.vue";

defineOptions({ name: "RouteStop" });

const props = withDefaults(
  defineProps<{
    stop: RouteStopDto;
    route: TripRouteDto;
    trip: TripDto;
    index: number | null;
    undoable?: boolean;
  }>(),
  {
    undoable: false,
  },
);

const emit = defineEmits<{
  (e: "change"): void;
}>();

const { t } = useI18n();

const { isRouteStopCancelled } = useTrip();
const { createOrderFreightFromRouteFreight } = useFreights();
const { createRouteActionsDisableRules } = useDisableRules();

const isEmpty = computed(() => !props.stop.id);

const isReload = computed(() => props.stop.isReload);

const isNew = computed(() => props.stop.status === RouteStopStatus.notStarted);

const isPickUp = computed(() => props.stop.type === OrderStopType.pickup);

const isDelivery = computed(() => props.stop.type === OrderStopType.delivery);

const isReadyForCheckIn = computed(() => {
  return Boolean(props.route.dispatchConfirmed) && props.stop.status === RouteStopStatus.inRoute;
});

const isReadyForLoading = computed(() => {
  return props.stop.status === RouteStopStatus.waitingForLoading && isPickUp.value;
});

const isReadyForLoadingVerification = computed(() => {
  return props.stop.status === RouteStopStatus.waitingForVerification && isPickUp.value;
});

const isReadyForUnloading = computed(() => {
  return props.stop.status === RouteStopStatus.waitingForLoading && isDelivery.value;
});

const isReadyForUnloadingVerification = computed(() => {
  return props.stop.status === RouteStopStatus.waitingForVerification && isDelivery.value;
});

const isReadyForGoodToGo = computed(() => props.stop.status === RouteStopStatus.waitingForGoodToGo);

const isComplete = computed(() => props.stop.status === RouteStopStatus.completed);

const isActive = computed(() => !isNew.value && !isComplete.value);

const isCheckedIn = computed(() => Boolean(props.stop.checkInTime));

const isLoaded = computed(() => {
  const statusesAfterLoad = [
    RouteStopStatus.waitingForVerification,
    RouteStopStatus.waitingForGoodToGo,
    RouteStopStatus.completed,
  ];

  return statusesAfterLoad.includes(props.stop.status);
});

const hasBols = computed(() => {
  return Boolean(props.stop.bols.length);
});

const showBolsAlert = computed(() => {
  return !hasBols.value && isLoaded.value && isPickUp.value && !isReload.value;
});

const showBolsButton = computed(() => {
  return !hasBols.value && isComplete.value && isPickUp.value && !isReload.value;
});

const stopType = computed(() => {
  switch (props.stop.type) {
    case OrderStopType.pickup:
      return "Pick up";
    case OrderStopType.delivery:
      return "Delivery";
    default:
      return "";
  }
});

const freights = computed(() => props.route.freights);

const stopFreights = computed<TripRouteFreight[]>(() => {
  return props.stop.freights
    .map((stopFreightId) => freights.value.find((freight) => freight.id === stopFreightId))
    .filter((freight): freight is TripRouteFreight => freight !== undefined);
});

const isUnloadVerified = computed(() => {
  const statusesAfterUnloadVerification = [RouteStopStatus.waitingForGoodToGo, RouteStopStatus.completed];

  return isDelivery.value && statusesAfterUnloadVerification.includes(props.stop.status);
});

const showPodAlert = computed(() => {
  if (!isUnloadVerified.value || props.stop.isReload) {
    return false;
  }

  return props.stop.bols.find((bol) => {
    return !Object.keys(bol.signedBy).includes(String(props.stop.id));
  });
});

const stopChangesFreights = computed<OrderFreightDto[]>(() => {
  return stopFreights.value.map((freight) => ({
    ...createOrderFreightFromRouteFreight(freight),
    oldQuantity: freight.oldPlannedQuantity,
    oldWeight: freight.oldPlannedWeight,
    oldType: freight.oldType,
    oldStackable: freight.oldStackable,
  }));
});

const distance = computed(() => {
  const nextStop = props.index === null ? undefined : props.route.routeStops[props.index - 1];

  return nextStop ? props.route.distances[`${props.stop.id}-${nextStop.id}`] : null;
});

const isCanceled = computed(() => isRouteStopCancelled(props.route, props.stop));

const isDeleted = computed(() => props.stop.isDeleted);

const isUpdated = computed(() => !isDeleted.value && props.stop.state === RouteStopState.updated);

const isPlanned = computed(() => props.route.status === RouteStatus.planned);

const isRouteCancelled = computed(() => props.route.status === RouteStatus.cancelled);

const stopClass = computed(() => {
  if (isEmpty.value) {
    return "bg-white border border-grey-lighten-1";
  }

  if (isCanceled.value) {
    return "bg-red-lighten-5";
  }

  if (isPlanned.value) {
    return "bg-indigo-lighten-5";
  }

  return "bg-grey-lighten-4";
});

const isLastStop = computed(() => {
  const activeRouteStops = props.route.routeStops.filter((routeStop) => routeStop.id && !routeStop.isDeleted);

  return activeRouteStops.findIndex((routeStop) => routeStop.id === props.stop.id) === activeRouteStops.length - 1;
});

const routeActionsDisableRules = computed<DisableRule[]>(() => {
  return createRouteActionsDisableRules({ route: props.route, stop: props.stop });
});

function onChange() {
  emit("change");
}
</script>

<template>
  <stepper-item
    :active="isActive"
    :complete="isComplete"
    :is-cancelled="isRouteCancelled"
    :is-deleted="isDeleted"
    :last="isLastStop"
    :number="index"
  >
    <div :class="stopClass" class="rounded pa-4 my-2 route-stop d-flex flex-column" data-qa="route-stop">
      <template v-if="isEmpty">
        <div class="d-flex align-center justify-center flex-column flex-1">
          <v-icon class="mb-3" color="grey" size="34">mdi-alert-octagon-outline</v-icon>
          <span class="font-size-16 text-grey">Not used in this route</span>
        </div>
      </template>

      <div v-else>
        <div class="d-flex align-center justify-space-between">
          <div class="d-flex align-center">
            <route-stop-icon
              :is-deleted="stop.isDeleted"
              :is-reload="stop.isReload"
              :type="stop.type"
              class="mr-2"
              size="20"
            ></route-stop-icon>

            <span v-if="isReload" class="font-size-12 text-blue-grey-darken-1 mr-1">Reload stop - </span>
            <div
              :class="isDeleted ? 'text-grey' : 'text-blue-grey-darken-1'"
              class="font-size-12 font-weight-600"
              data-qa="route-stops-stop-type"
            >
              {{ stopType }} #{{ stop.number }}
            </div>
            <router-link
              v-if="!isDeleted && stop.order"
              :to="{ name: 'order-view', params: { id: stop.order.id } }"
              class="d-block text-primary font-weight-500 font-size-12 text-decoration-none ml-3"
              data-qa="route-stops-order-number"
              target="_blank"
            >
              Order #{{ stop.order.number }}
            </router-link>
            <span v-if="isDeleted && stop.order" class="text-grey font-weight-500 font-size-12 ml-3">
              Order #{{ stop.order.number }}
            </span>
            <stop-changes-popover v-if="isUpdated" :freights="stopChangesFreights" :stop="stop"></stop-changes-popover>
          </div>
          <div class="d-flex align-center">
            <stop-timer
              v-if="isCheckedIn"
              :end-time="stop.checkOutTime"
              :start-time="stop.checkInTime"
              class="mr-4"
            ></stop-timer>
            <route-stop-actions
              v-if="(undoable || isComplete) && !isCanceled"
              :route="route"
              :stop="stop"
              :trip="trip"
              class="ml-3"
              @change="onChange"
            ></route-stop-actions>
          </div>
          <div v-if="isUpdated" class="badge bg-warning pa-2 rounded-te rounded-bs font-weight-600">UPDATED</div>
          <div v-if="isDeleted" class="badge bg-grey-darken-1 pa-2 rounded-te rounded-bs font-weight-600">DELETED</div>
        </div>
        <route-stop-status-label
          v-if="!isDeleted"
          :is-cancelled="isCanceled"
          :stop="stop"
          class="mt-2"
        ></route-stop-status-label>
        <alert-message
          v-if="isDeleted"
          bg-color="indigo-lighten-5"
          border-color="none"
          class="mt-3"
          color="indigo"
          icon="mdi-information-outline"
          icon-color="indigo"
        >
          Stop will be removed from trip after re-dispatch confirmation
        </alert-message>
        <facility-info
          v-if="stop.facility"
          :disabled="isDeleted"
          :facility="stop.facility"
          :updated="false"
          class="mt-4"
        ></facility-info>
        <div :class="isDeleted ? 'text-grey' : 'text-grey-darken-1'" class="d-flex font-weight-500 mt-5">
          <div class="mr-2">
            <v-icon icon="mdi-clock-outline" size="20"></v-icon>
          </div>
          <order-actions-time
            :check-in-time="stop.checkInTime"
            :check-out-time="stop.checkOutTime"
            :load-unload-time="stop.loadUnloadTime"
            :stop-type="stop.type"
            :time-to="stop.timeTo"
            :time-type="stop.timeType"
            :timezone="stop.timezone"
            style="margin-top: 0.1rem"
          />
        </div>
        <route-stop-freight-details
          :disabled="isDeleted"
          :freights="stopFreights"
          :stop="stop"
        ></route-stop-freight-details>

        <tooltip-button :rules="routeActionsDisableRules">
          <template #default="{ disabled }">
            <check-in-action
              v-if="isReadyForCheckIn"
              :disabled="disabled"
              :route="route"
              :stop="stop"
              :trip="trip"
              class="mt-6 text-uppercase font-weight-600"
              @checked-in="onChange"
            ></check-in-action>
            <load-action
              v-if="isReadyForLoading"
              :disabled="disabled"
              :route="route"
              :stop="stop"
              :trip="trip"
              class="mt-6 text-uppercase font-weight-600"
              color="primary"
              @load="onChange"
            ></load-action>
            <load-verification-action
              v-if="isReadyForLoadingVerification"
              :disabled="disabled"
              :route="route"
              :stop="stop"
              :trip="trip"
              class="mt-6"
              @verify="onChange"
            ></load-verification-action>
            <unload-action
              v-if="isReadyForUnloading"
              :disabled="disabled"
              :route="route"
              :stop="stop"
              :trip="trip"
              class="mt-6"
              @unload="onChange"
            ></unload-action>
            <unload-verification-action
              v-if="isReadyForUnloadingVerification"
              :disabled="disabled"
              :route="route"
              :stop="stop"
              :trip="trip"
              class="mt-6"
              @verify="onChange"
            ></unload-verification-action>
            <v-row v-if="isReadyForGoodToGo" class="mt-6">
              <v-col>
                <show-info-action
                  :disabled="disabled"
                  :route="route"
                  :stop="stop"
                  :trip="trip"
                  @save="onChange"
                ></show-info-action>
              </v-col>
              <v-col>
                <good-to-go-action
                  v-if="!isLastStop"
                  :disabled="disabled"
                  :route="route"
                  :stop="stop"
                  :trip="trip"
                  @finish="onChange"
                ></good-to-go-action>
                <finish-action
                  v-else
                  :disabled="disabled"
                  :route="route"
                  :stop="stop"
                  :trip="trip"
                  @finish="onChange"
                ></finish-action>
              </v-col>
            </v-row>
          </template>
        </tooltip-button>

        <alert-message v-if="showBolsAlert" class="mt-5">BOL not uploaded yet</alert-message>

        <add-bol-action v-if="showBolsButton" :route="route" :stop="stop" :trip="trip" class="mt-2" @add="onChange" />

        <template v-if="showPodAlert">
          <alert-message class="mt-5">{{ t("trip.pod-not-signed") }}</alert-message>

          <sign-pod-action :route="route" :stop="stop" :trip="trip" class="mt-2" @sign="onChange" />
        </template>
      </div>
    </div>
    <distance-line v-if="distance" :value="distance"></distance-line>
  </stepper-item>
</template>

<style lang="scss" scoped>
.route-stop {
  min-height: 270px;
}

.badge {
  margin-top: -16px;
  margin-right: -16px;
}
</style>
